import { Component, ElementRef, inject, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { CategoryService } from '../../features/categories/category.service';
import { ToastrService } from 'ngx-toastr';
import { TagsLanguageService } from '../../features/tags-language/tagsLanguage.service';
import { ActivatedRoute, Router } from '@angular/router';
import { StoriesService } from '../../features/stories/stories.service';
import { FullscreenLoaderComponent } from '../../shared/components/loader-component/loader.component';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialog } from '../../shared/components/confirm-dialog/confirm-dialog';
import { forkJoin } from 'rxjs';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  Storage,
  ref,
  uploadBytes,
  getDownloadURL,
} from '@angular/fire/storage';
import {
  noOnlySpacesValidator,
  noOnlySpecialCharsValidator,
} from '../../core/helpers/validator';
import { ContentLoaderComponent } from '../../shared/components/content-loader-component/contentLoader.component';
import { FilenameGeneratorUtil } from '../../core/utils/filename-generator';

@Component({
  selector: 'app-new-video-story',
  imports: [
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatSelectModule,
    CommonModule,
    FullscreenLoaderComponent,
    ContentLoaderComponent,
  ],
  templateUrl: './new-video-story.component.html',
  styleUrl: './new-video-story.component.scss',
})
export class NewVideoStoryComponent {
  @ViewChild('thumbfileInput') thumbfileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('videofileInput') videofileInput!: ElementRef<HTMLInputElement>;
  readonly dialog = inject(MatDialog);
  readonly storage = inject(Storage);
  public translationForm: FormGroup;
  public videoContentForm: FormGroup;
  public tags: any[] = [];
  public selectedTags: any[] = [];
  public languages: any[] = [];
  public langCode: String = '';
  public videoTranslations: {
    langId: number;
    name: string;
    id?: number;
  }[] = [];
  categories: any[] = [];
  videoUrl: string | null = null;
  videoUploading = false;
  videoFileName: string | null = null;
  thumbUrl: string | null = null;
  thumbUploading = false;
  thumbFileName: string | null = null;
  loading = false;
  videoId: any;
  isSubmitting = false;
  contentLoading: boolean = false;

  constructor(
    private toastr: ToastrService,
    private tagsLanguageService: TagsLanguageService,
    private router: Router,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private categoryService: CategoryService,
    private storiesService: StoriesService
  ) {
    this.translationForm = this.fb.group({
      name: [
        '',
        [
          Validators.required,
          Validators.maxLength(50),
          noOnlySpacesValidator(),
          noOnlySpecialCharsValidator(),
        ],
      ],
      langId: [1, Validators.required],
      translationId: [null]
    });

    this.videoContentForm = this.fb.group({
      tags: [[]],
      videoUrl: [''],
    });
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      this.videoId = params.get('id');
      if (this.videoId) {
        this.contentLoading = true;
        this.translationForm.get('langId')?.patchValue(null);
      }

      forkJoin({
        languages: this.tagsLanguageService.getLangauges(),
        tags: this.tagsLanguageService.getAllTagsWithFilter(this.langCode),
      }).subscribe({
        next: ({ languages, tags }) => {
          if (languages?.data) {
            this.languages = languages.data;
          }

          if (tags?.data) {
            this.tags = tags.data
              .filter((tag: any) => tag.type === 'story')
              .map((tag: any) => {
                const translation = tag.tagTranslation?.[0];
                return translation
                  ? { ...tag, name: translation.tagName }
                  : null;
              })
              .filter((tag: any) => tag !== null);
          }

          if (this.videoId) {
            this.getVideoContentById(this.videoId);
          }
        },
        error: (err) => {
          console.error('Failed to load initial data:', err);
        },
      });
    });
  }

  videoSource: 'upload' | 'url' = 'upload';
  setVideoSource(source: 'upload' | 'url') {
    if (this.videoSource !== source) {
      this.videoSource = source;

      if (source === 'url') {
        this.clearVideoUpload();
        this.videoContentForm.patchValue({ videoUrl: '' });
      } else if (source === 'upload') {
        this.videoContentForm.patchValue({ videoUrl: '' });
      }
    }
  }

  getAllTags(lang: any): void {
    this.tagsLanguageService.getAllTagsWithFilter(lang).subscribe({
      next: (res) => {
        if (res?.data) {
          this.tags = res.data
            .filter((tag: any) => tag.type === 'story')
            .map((tag: any) => {
              const translation = tag.tagTranslation?.[0];
              return translation ? { ...tag, name: translation.tagName } : null;
            })
            .filter((tag: any) => tag !== null);
        }
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  isTagSelected(tag: any): boolean {
    const selectedTags = this.videoContentForm.get('tags')?.value || [];
    return selectedTags.some((t: any) => t.id === tag.id);
  }

  toggleTagSelection(tag: any): void {
    const control = this.videoContentForm.get('tags');
    const current = control?.value || [];

    const index = current.findIndex((t: any) => t.id === tag.id);

    if (index === -1) {
      control?.setValue([...current, tag]);
    } else {
      const updated = current.filter((t: any) => t.id !== tag.id);
      control?.setValue(updated);
    }
  }

  getLanguages(): void {
    this.tagsLanguageService.getLangauges().subscribe({
      next: (res) => {
        if (res?.data) {
          this.languages = res?.data;
        }
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  getCategoryByLangId(langId: any): void {
    this.categoryService.getCategoryByLang(langId).subscribe({
      next: (res) => {
        this.categories = res.data;
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  getLanguageName(langId: number): string {
    const lang = this.languages.find((l) => l.id === langId);
    return lang ? lang.name : `Lang ID: ${langId}`;
  }

  onLanguageSelect(event: MatSelectChange): void {
    const selectedId = event.value;
    this.langCode = event.value;
    const selectedLang = this.languages.find((lang) => lang.id === selectedId);
    if (selectedLang) {
      this.langCode = selectedLang.code;
      this.getAllTags(this.langCode);
    }
  }

  isLangUsed(langId: number): boolean {
    return this.videoTranslations?.some((t) => t.langId === langId);
  }

  getVideoContentById(id: any, lang?: any) {
    this.storiesService.getVideoStoriesById(id, '').subscribe({
      next: (res) => {
        const storyData = res.data;

        storyData.translations.forEach((translation: any) => {
          this.videoTranslations.push({
            langId: translation.langId,
            name: translation.name,
            id: translation.id,
          });
        });

        const selectedTagObjects = this.tags.filter((tag) =>
          storyData.tags.some((selectedTag: any) => selectedTag.id === tag.id)
        );
        this.videoContentForm.get('tags')?.setValue(selectedTagObjects);

        // Handle thumbnail with utility
        this.thumbUrl = storyData.thumbURL || null;
        this.thumbFileName = storyData.thumbURL
          ? FilenameGeneratorUtil.generateRandomFilename(storyData.thumbURL)
          : null;

        // Handle video source with utility
        if (storyData.videoLink?.includes('firebasestorage')) {
          this.videoUrl = storyData.videoLink;
          this.videoFileName = FilenameGeneratorUtil.generateRandomFilename(storyData.videoLink);
          this.videoSource = 'upload';
        } else {
          this.videoUrl = null;
          this.videoFileName = null;
          this.videoContentForm.patchValue({ videoUrl: storyData.videoLink });
          this.videoSource = 'url';
        }
        this.contentLoading = false;
      },
      error: (err) => {
        console.error(err);
        this.contentLoading = false;
      },
    });
  }
  onDeleteCategory(id: any): void {
    const dialogRef = this.dialog.open(ConfirmDialog);
    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.removeVideoTranslation(id);
      }
    });
  }

  removeVideoTranslation(index: number): void {
    this.storiesService
      .deleteVideoStory(this.videoTranslations[index].id)
      .subscribe({
        next: (res) => {
          console.log(res);
        },
        error: (err) => {
          console.log(err);
        },
      });
    this.videoTranslations.splice(index, 1);
    this.translationForm.get('name')?.enable();
    this.translationForm.get('langId')?.enable();
  }

  editVideoTranslation(index: number): void {
    const selected = this.videoTranslations[index];
    this.videoTranslations.splice(index, 1);

    this.translationForm.patchValue({
      name: selected.name,
      langId: selected.langId,
      translationId: selected.id || null
    });

    this.translationForm.get('name')?.enable();
    this.translationForm.get('langId')?.enable();

    const selectedLang = this.languages.find(
      (lang) => lang.id === selected.langId
    );
    if (selectedLang) {
      this.langCode = selectedLang.code;
      this.getAllTags(this.langCode);
    }

    this.translationForm.get('name')?.markAsTouched();
    this.translationForm.get('langId')?.markAsTouched();
    this.scrollUp(`translation`);
  }

  addVideoTranslation(): void {
    const { name, langId, translationId } = this.translationForm.value;

    if (this.translationForm.invalid) {
      this.toastr.warning('Please check all the input fields');
      return;
    }

    const isDuplicate = this.videoTranslations.some((t) => t.langId === langId);
    if (isDuplicate) {
      this.toastr.error('This language is already added');
      return;
    }

    const translationToAdd: any = {
      name,
      langId,
    };

    if (translationId) {
      translationToAdd.id = translationId;
    }

    this.videoTranslations.push(translationToAdd);

    this.translationForm.reset();
    this.translationForm.patchValue({
      langId: null,
      translationId: null
    });

    if (this.videoTranslations.length === this.languages.length) {
      this.translationForm.get('name')?.disable();
      this.translationForm.get('langId')?.disable();
    }
  }

  scrollUp(elementId: string): void {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }

  async onVideoUpload(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;

    const allowedTypes = ['video/mp4', 'video/quicktime'];
    const maxSize = 300 * 1024 * 1024;

    if (!allowedTypes.includes(file.type)) {
      this.toastr.error('Choose proper format (.mp4 or .mov)');
      input.value = '';
      return;
    }

    if (file.size > maxSize) {
      this.toastr.error('Choose a smaller video (less than 300 MB)');
      input.value = '';
      return;
    }

    this.videoUploading = true;
    if (!navigator.onLine) {
      this.toastr.warning(
        'No internet connection. Please check your connection and try again.'
      );
      input.value = '';
      this.videoUploading = false;
      return;
    }

    const filePath = `video-story/${file.name}_${Date.now()}`;
    const fileRef = ref(this.storage, filePath);

    try {
      await uploadBytes(fileRef, file);
      if (!navigator.onLine) {
        input.value = '';
        this.toastr.warning('Upload failed. No internet connection');
        this.videoUploading = false;
        return;
      }

      this.videoUrl = await getDownloadURL(fileRef);
      this.videoFileName = file.name;
    } catch (err) {
      this.toastr.error(
        'Upload failed due to an internal error. Please try again later.'
      );
    } finally {
      this.videoUploading = false;
    }
  }

  clearVideoUpload(): void {
    this.videoFileName = null;
    this.videoUrl = null;
    if (this.videofileInput) {
      this.videofileInput.nativeElement.value = '';
    }
  }

  async onThumbUpload(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const maxSize = 15 * 1024 * 1024;

    if (!allowedTypes.includes(file.type)) {
      this.toastr.error('Choose proper Image format');
      input.value = '';
      return;
    }

    if (file.size > maxSize) {
      this.toastr.error('Choose a smaller image file (less than 15 MB)');
      input.value = '';
      return;
    }

    this.thumbUploading = true;
    if (!navigator.onLine) {
      this.toastr.warning(
        'No internet connection. Please check your connection and try again.'
      );
      input.value = '';
      this.thumbUploading = false;
      return;
    }

    const filePath = `story-thumb/${file.name}_${Date.now()}`;
    const fileRef = ref(this.storage, filePath);

    try {
      await uploadBytes(fileRef, file);
      if (!navigator.onLine) {
        input.value = '';
        this.toastr.warning('Upload failed. No internet connection');
        this.thumbUploading = false;
        return;
      }

      this.thumbUrl = await getDownloadURL(fileRef);
      this.thumbFileName = file.name;
    } catch (err) {
      this.toastr.error(
        'Upload failed due to an internal error. Please try again later.'
      );
      console.error('Upload failed--', err);
    } finally {
      this.thumbUploading = false;
    }
  }

  clearThumbUpload(): void {
    this.thumbFileName = null;
    this.thumbUrl = null;
    if (this.thumbfileInput) {
      this.thumbfileInput.nativeElement.value = '';
    }
  }

  onSubmit(): void {
    if (this.videoContentForm.invalid) {
      this.toastr.warning('Please fill out all required fields correctly.');
      return;
    }

    const tags = this.videoContentForm.value.tags.map((tag: any) => tag.id);

    if (this.videoTranslations.length === 0) {
      this.toastr.warning('Please add at least one translation');
      return;
    }

    if (!this.videoTranslations.some((translation) => translation.langId === 1)) {
      this.toastr.error(
        'English is mandatory.Please add it to continue.'
      );
      return;
    }

    if (tags.length === 0) {
      this.toastr.warning('Please add at least one tag');
      return;
    }

    const videoLink = this.videoUrl || this.videoContentForm.value.videoUrl;

    if (!videoLink && !this.thumbUrl) {
      this.toastr.warning('Please attach a video and a thumbnail image.');
      return;
    }

    if (!this.thumbUrl) {
      this.toastr.warning('Please attach a thumbnail image.');
      return;
    }

    if (!videoLink) {
      this.toastr.warning('Please provide a video link or upload a video.');
      return;
    }

    const payload = {
      categoryId: 2, //default value 2 is given for video
      thumbURL: this.thumbUrl,
      rating: 0.0,
      videoLink: videoLink,
      tags: tags,
      translations: this.videoTranslations,
    };

    this.loading = true;
    this.isSubmitting = true;

    if (this.videoId) {
      this.storiesService.updateVideoStory(this.videoId, payload).subscribe({
        next: (res) => {
          this.toastr.success('Video story successfully updated');
          this.videoContentForm.reset();
          this.translationForm.reset();
          this.isSubmitting = false;
          this.router.navigate(['/video']);
        },
        error: (err) => {
          this.loading = false;
          this.isSubmitting = false;
        },
        complete: () => {
          this.loading = false;
          this.isSubmitting = false;
        },
      });
      return;
    } else {
      this.storiesService.createVideoStory(payload).subscribe({
        next: (res) => {
          console.log(res);
          this.toastr.success(res.message);
          this.videoContentForm.reset();
          this.translationForm.reset();
          this.router.navigate(['/video']);
          this.isSubmitting = false;
        },
        error: (err) => {
          console.log(err);
          this.isSubmitting = false;
          this.loading = false;
        },
        complete: () => {
          this.loading = false;
          this.isSubmitting = false;
        },
      });
    }
  }

  onCancel() {
    this.router.navigate(['/video']);
  }
}
